/**
 * Provider Key section — the Providers tab's BYOK surface.
 *
 * Shows whether the account has its own API key for the provider (masked
 * hint + last-validated, NEVER the key), a password input to set/replace it
 * (validated live against the provider before storage), a delete control,
 * and the byok_mode policy toggle (context ADR-032):
 *   byok_preferred — account key if set, else the platform key
 *   byok_only      — account key required; runs fail loud without one
 *
 * Rendered ONLY for BYOK-capable providers AND account admins (the routes
 * 403 non-admins). The input is a password field
 * with autofill disabled and is submitted only by the explicit Set button;
 * the plaintext is cleared from the form on success and never
 * rendered back.
 */

import { useState } from 'react';
import { Button } from '@localagent/ui/components/button';
import { Input } from '@localagent/ui/components/input';
import { Badge } from '@localagent/ui/components/badge';
import { cn } from '@localagent/ui/lib/utils';
import type { ProviderType, ByokMode } from '@localagent/sdk';
import {
  useProviderCredentialStatus,
  useSetProviderCredential,
  useDeleteProviderCredential,
  useSetByokMode,
} from '../../hooks/useProviderManagement';

export function ProviderKeySection({ provider }: { provider: ProviderType }) {
  const status = useProviderCredentialStatus(provider);
  const setCredential = useSetProviderCredential();
  const deleteCredential = useDeleteProviderCredential();
  const setByokMode = useSetByokMode();

  const [keyInput, setKeyInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const busy =
    setCredential.isPending || deleteCredential.isPending || setByokMode.isPending;

  const handleSet = () => {
    if (!keyInput.trim() || busy) return;
    setError(null);
    setCredential.mutate(
      { provider, apiKey: keyInput.trim() },
      {
        onSuccess: () => setKeyInput(''), // plaintext never lingers
        onError: (e) => setError(e.message),
      },
    );
  };

  const handleDelete = () => {
    if (busy) return;
    setError(null);
    deleteCredential.mutate(
      { provider },
      { onError: (e) => setError(e.message) },
    );
  };

  const handleMode = (mode: ByokMode) => {
    if (busy || status.data?.byok_mode === mode) return;
    setError(null);
    setByokMode.mutate({ provider, mode }, { onError: (e) => setError(e.message) });
  };

  const s = status.data;

  return (
    <div className="rounded-md bg-card p-3" data-testid="provider-key-section">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold">Provider key</h3>
        {s?.is_set ? (
          <Badge variant="secondary" data-testid="provider-key-badge">
            Account key
          </Badge>
        ) : (
          <Badge variant="outline" data-testid="provider-key-badge">
            Platform key
          </Badge>
        )}
      </div>

      {/* Masked status — never the key. */}
      <p className="mb-2 text-xs text-muted-foreground" data-testid="provider-key-status">
        {status.isLoading
          ? 'Loading…'
          : s?.is_set
            ? `•••• ${s.key_hint ?? ''}${
                s.last_validated_at
                  ? ` · validated ${new Date(s.last_validated_at).toLocaleString()}`
                  : ''
              }`
            : 'No account key set — calls use the platform key while the policy allows it.'}
      </p>

      <div className="flex items-center gap-2">
        <Input
          type="password"
          autoComplete="off"
          placeholder={s?.is_set ? 'Replace API key…' : 'Paste API key…'}
          value={keyInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKeyInput(e.target.value)}
          disabled={busy}
          data-testid="provider-key-input"
          className="h-8 text-xs"
        />
        <Button
          size="sm"
          className="h-8 text-xs"
          onClick={handleSet}
          disabled={busy || !keyInput.trim()}
          data-testid="provider-key-set"
        >
          {setCredential.isPending ? 'Validating…' : 'Set'}
        </Button>
        {s?.is_set && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-destructive hover:text-destructive"
            onClick={handleDelete}
            disabled={busy}
            data-testid="provider-key-delete"
          >
            Delete
          </Button>
        )}
      </div>

      {error && (
        <p className="mt-2 text-sm text-destructive" data-testid="provider-key-error">
          {error}
        </p>
      )}

      {/* byok_mode policy (context ADR-032). */}
      <div className="mt-3 flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Key policy:</span>
        <div className="flex rounded-md bg-muted" data-testid="byok-mode-toggle">
          {(
            [
              { mode: 'byok_preferred' as ByokMode, label: 'Prefer account key' },
              { mode: 'byok_only' as ByokMode, label: 'Account key only' },
            ]
          ).map(({ mode, label }) => (
            <button
              key={mode}
              type="button"
              disabled={busy}
              onClick={() => handleMode(mode)}
              data-testid={`byok-mode-${mode}`}
              data-active={s?.byok_mode === mode}
              className={cn(
                'px-2 py-1 text-xs transition-colors first:rounded-l-md last:rounded-r-md',
                s?.byok_mode === mode
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-background',
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {s?.byok_mode === 'byok_only' && !s.is_set && (
        <p className="mt-2 text-xs text-destructive" data-testid="byok-only-warning">
          Account-key-only is set but no key is stored — runs on this provider will fail
          until a key is added.
        </p>
      )}
    </div>
  );
}
